import { useEffect, useState } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import TableSkeleton from "@/components/skeleton/TableSkeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Pagination from "@/components/Pagination";
import {
  Dialog,
  DialogDescription,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";
import DatePicker from "@/components/ui/DatePicker";

// Icons
import { PlusCircle, Eye, Save } from "lucide-react";

// Services
import { getLandAssets, updateLandAsset } from "@/services/soe";
import { getLandStatus } from "@/services/master";
import type { LandAssetList } from "@/types/soe";
import { S3_BUCKET_LINK } from "@/components/constants";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/land/"
)({
  component: Land,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      activeStatus: search.activeStatus as string | undefined,
      page: Number(search.page) || 1,
    };
  },
});

function Land() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { company_management } = useAccessPermission();
  const companyId = String(localStorage.getItem("companyId")).replace(
    /['"]+/g,
    ""
  ); // Remove quotes from string

  // States
  const [selectedItem, setSelectedItem] = useState({} as LandAssetList);
  const [editFormData, setEditFormData] = useState({
    status: "",
    saleDoc: "",
    saleDate: "",
  });
  const [saleValue, setSaleValue] = useState(
    selectedItem?.saleValue?.toString() || ""
  );

  const [editModal, setEditModal] = useState(false);

  // Queries
  const {
    data: landStatusRes,
    isLoading: landStatusLoading,
    error: landStatusResError,
  } = useQuery({
    queryKey: ["landStatus", companyId],
    queryFn: () => getLandStatus(),
  });

  const [{ data, isLoading, error: landsError }] = useQueries({
    queries: [
      {
        queryKey: ["lands", companyId, search.activeStatus, search.page],
        queryFn: () =>
          getLandAssets({
            companyId: companyId,
            limit: 100,
            skip: 0,
            landStatusId: search.activeStatus as string,
          }),
        enabled: !!search.activeStatus,
      },
    ],
  });

  if (landStatusResError || landsError) {
    const isAxiosError = (
      error: unknown
    ): error is { response?: { data?: { message?: string } } } => {
      return typeof error === "object" && error !== null && "response" in error;
    };

    if (
      (isAxiosError(landStatusResError) &&
        landStatusResError?.response?.data?.message === "LOGGED_OUT") ||
      (isAxiosError(landsError) &&
        landsError?.response?.data?.message === "LOGGED_OUT")
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Effects
  useEffect(() => {
    if (landStatusRes?.data?.length > 0) {
      navigate({
        search: {
          activeStatus: landStatusRes?.data[1]?.id,
          page: 1,
        },
        replace: true,
      });
    }
  }, [landStatusRes, navigate]);

  // Data
  const lands = data?.data as LandAssetList[];
  const landStatus = landStatusRes?.data as {
    id: string;
    name: string;
  }[];
  const loading = isLoading || landStatusLoading;

  // Mutation
  const updateLandAssetMutation = useMutation<
    unknown,
    unknown,
    { [key: string]: string | number }
  >({
    mutationFn: (data: { [key: string]: string | number }) =>
      updateLandAsset(selectedItem?.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["lands", companyId],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ແກ້ໄຂຂໍ້ມູນທີ່ດິນສຳເລັດ",
      });

      setEditModal(false);
      setEditFormData({
        status: "",
        saleDoc: "",
        saleDate: "",
      });
    },
    onError: () => {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  // Functions
  const handleTabChange = (value: string) => {
    navigate({
      search: {
        activeStatus: value,
        page: 1,
      },
      replace: true,
    });
  };

  const handlePageChange = (page: number) => {
    navigate({
      search: {
        ...search,
        page,
      },
      replace: true,
    });
  };

  const formatSaleValue = (value: string) => {
    if (!value) return "";

    // Remove any existing commas and handle empty/invalid input
    const cleanValue = value.replace(/,/g, "");

    // Only allow numbers with up to 2 decimal places
    const isValidInput = /^\d*\.?\d{0,2}$/.test(cleanValue);
    if (!isValidInput && value !== "") return saleValue;

    // Split number into integer and decimal parts
    const [integerPart, decimalPart] = cleanValue.split(".");

    // Format integer part with commas
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Return formatted number with decimal part if it exists
    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };

  if (loading) {
    return <TableSkeleton />;
  }

  return (
    <div className="space-y-[24px]">
      <div className="text-[18px]">
        <h2 className="text-zinc-950">ຂໍ້ມູນທີ່ດິນ</h2>
        <p className="text-zinc-500">ຕາຕະລາງຂໍ້ມູນທີ່ດິນຂອງວິສາຫະກິດ</p>

        <Separator className="my-6" />
      </div>

      <div className="flex justify-end w-full">
        {company_management.isWrite && (
          <Button
            onClick={() =>
              navigate({
                to: "/account-management/soe/land/mutate",
                params: { id: companyId },
                search: {
                  id: undefined,
                  data: undefined,
                },
              })
            }
          >
            <PlusCircle className="mr-2" />
            ເພີ່ມລາຍການ
          </Button>
        )}
      </div>

      <Tabs
        value={search.activeStatus}
        onValueChange={(value) => handleTabChange(value)}
        className="flex flex-col gap-y-12"
      >
        <TabsList className="flex flex-wrap items-start justify-start gap-2 bg-transparent">
          {[...(landStatus || [])]?.reverse()?.map((status) => (
            <TabsTrigger
              key={status.id}
              value={status.id}
              className="px-3 bg-gray-100 rounded-full text-black data-[state=active]:bg-green-500 data-[state=active]:text-white"
            >
              {status.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Table className="w-full overflow-y-hidden border rounded-md">
        <TableHeader className="">
          <TableRow>
            {tableData.headers.map((header) => (
              <TableHead className="text-nowrap" key={header}>
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {lands?.map((item, index) => (
            <TableRow key={item?.id} className="cursor-pointer">
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/land/$detailId",
                    params: { detailId: item?.id },
                    search: {
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="text-nowrap"
              >
                {index + 1}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/land/$detailId",
                    params: { detailId: item?.id },
                    search: {
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="text-nowrap"
              >
                <div className="flex items-center gap-2">
                  <Eye className="text-blue-500" />
                  {item?.title}
                </div>
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/land/$detailId",
                    params: { detailId: item?.id },
                    search: {
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="text-nowrap"
              >
                {item?.landType}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/land/$detailId",
                    params: { detailId: item?.id },
                    search: {
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="text-nowrap"
              >
                {item?.totalArea}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/land/$detailId",
                    params: { detailId: item?.id },
                    search: {
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="text-nowrap"
              >
                {item?.location}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/land/$detailId",
                    params: { detailId: item?.id },
                    search: {
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="text-nowrap"
              >
                {item?.landFundType?.name}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/land/$detailId",
                    params: { detailId: item?.id },
                    search: {
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="text-nowrap"
              >
                {item?.initialValue?.toLocaleString()}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/land/$detailId",
                    params: { detailId: item?.id },
                    search: {
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="text-nowrap"
              >
                {item?.landStatus?.name === "ຂາຍອອກ"
                  ? item?.saleValue?.toLocaleString()
                  : item?.currentValue?.toLocaleString()}
              </TableCell>
              <TableCell className="text-nowrap">
                {company_management.isUpdate && (
                  <Button
                    onClick={() => {
                      setSelectedItem(item);
                      setEditModal(true);
                      setEditFormData((prev) => ({
                        ...prev,
                        status: "",
                        saleDoc: "",
                      }));
                    }}
                    variant={"outline"}
                  >
                    ອັບເດດສະຖານະ
                  </Button>
                )}
              </TableCell>
              {item?.landStatus?.name === "ຂາຍອອກ" ? (
                <TableCell className="text-nowrap">
                  <a
                    download={S3_BUCKET_LINK + item?.docFile}
                    href={S3_BUCKET_LINK + item?.docFile}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500 underline"
                  >
                    ເອກະສານຕິດຂັດ
                  </a>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        handlePageClick={handlePageChange}
        totalItems={data?.total || 0}
        itemsPerPage={40}
        currentPage={search.page}
      />

      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ແກ້ໄຂສະຖານະທີ່ດີນ</DialogTitle>
            <DialogDescription>
              ກາລຸນາກວດສອບຂໍ້ມູນຂອງທ່ານໃຫ້ຖືກຕ້ອງ ກ່ອນກົດບັນທຶກ
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();

              if (!editFormData?.saleDoc) {
                toast({
                  title: "ຫຼົ້ມເຫຼວ",
                  description: "ກະລຸນາອັບໂຫຼດເອກະສານ",
                  variant: "destructive",
                });

                return;
              }

              const payload = {
                landStatusId: editFormData.status,
                saleDoc: editFormData.saleDoc,
                saleValue: saleValue.replace(/,/g, ""), // Remove commas before sending
                saleDate: editFormData.saleDate,
                id: selectedItem.id,
              };

              console.log("Payload", payload);

              updateLandAssetMutation.mutate(payload);
            }}
            className="grid gap-4 py-4"
          >
            <div>
              <Label htmlFor="landStatusid">ສະຖານະ</Label>
              <Select
                name="landStatusId"
                onValueChange={(value) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    status: value,
                  }))
                }
                required
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="ເລືອກເມືອງ" />
                </SelectTrigger>
                <SelectContent>
                  {landStatus?.map((item) => (
                    <SelectItem value={item.id} key={item.id}>
                      {item?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-start gap-2">
                <Label htmlFor="saleValue" className="text-right">
                  ລາຄາຂາຍອອກ <span className="text-red-500">*</span>
                </Label>
                {/* <Input placeholder="ປ້ອນລາຄາຂາຍອອກ..." id="saleValue" name="saleValue" type="text" required defaultValue={selectedItem?.saleValue} /> */}

                <Input
                  placeholder="ປ້ອນລາຄາຂາຍອອກ..."
                  id="saleValue"
                  name="saleValue"
                  type="text"
                  required
                  value={formatSaleValue(saleValue)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, "");
                    // Only allow numbers and exactly two decimal places
                    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
                      // If there's more than one decimal point, keep only the first one
                      const parts = value.split(".");
                      if (parts.length > 2) {
                        setSaleValue(parts[0] + "." + parts[1]);
                      } else {
                        setSaleValue(value);
                      }
                    }
                  }}
                />
              </div>
              <DatePicker
                label={
                  <Label htmlFor="saleDate" className="text-right">
                    ວັນທີຂາຍອອກ <span className="text-red-500">*</span>
                  </Label>
                }
                name="saleDate"
                id="saleDate"
                onChange={(value) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    saleDate: value,
                  }))
                }
                value={editFormData?.saleDate || ""}
                required
              />
            </div>

            <div>
              <FileUpload
                currentFile={editFormData?.saleDoc}
                onFileUpload={(file) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    saleDoc: file,
                  }))
                }
              />
            </div>

            <DialogFooter>
              <DialogClose>
                <Button variant={"outline"} type="button">
                  ຍົກເລີກ
                </Button>
              </DialogClose>
              <Button type="submit">
                {updateLandAssetMutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="mr-2" />
                    ບັນທຶກຂໍ້ມູນ
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const tableData = {
  headers: [
    "ລຳດັບ",
    "ເນື້ອໃນທີ່ດິນ",
    "ປະເພດທີ່ດິນ",
    "ເນື້ອທີ່ທັງໝົດ (ມ2/ຮຕ)",
    "ທີ່ຕັ້ງຂອງດິນ",
    "ທຶນໃນການຈັດຊື້",
    "ມູນຄ່າເບື້ອງຕົ້ນ",
    "ມູນຄ່າປັດຈຸບັນ",
    "ຈັດການ",
  ],
};
